import React, { useCallback } from "react";
import {
    LinkedAccrualDocument,
    LinkedBill,
    LinkedInvoice,
} from "@common/types/financialDocument";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { createBillsUrl } from "@components/bills/lib/billsUrl";
import { createInvoicesUrl } from "@components/invoices/lib/invoicesUrl";
import { useDocumentDetails } from "../../useDocumentDetails";
import { FinancialDocumentLinkRow } from "./FinancialDocumentLinkRow";

type AccrualDocumentType = "invoice" | "bill";

interface Props {
    linkedAccrualDocument: LinkedInvoice | LinkedBill;
    type: AccrualDocumentType;
}

function formatAccrualDescription(
    linkedAccrualDocument: LinkedAccrualDocument,
): string {
    const numberLabel = linkedAccrualDocument.invoiceNumber;
    const secondaryLabel =
        linkedAccrualDocument.counterpartyName ??
        linkedAccrualDocument.description;

    if (numberLabel && secondaryLabel) {
        return `${numberLabel} · ${secondaryLabel}`;
    }

    return numberLabel ?? secondaryLabel ?? "Untitled";
}

export const FinancialAccrualDocumentLink: React.FC<Props> = ({
    linkedAccrualDocument,
    type,
}) => {
    const navigate = useStateAwareNavigate();
    const { shownDocumentId, closeDetails } = useDocumentDetails();

    const handleClick = useCallback(() => {
        if (shownDocumentId) {
            closeDetails();
        }

        if (type === "invoice") {
            navigate(
                createInvoicesUrl({
                    invoiceIds: [linkedAccrualDocument.id],
                }),
            );
            return;
        }

        navigate(
            createBillsUrl({
                billIds: [linkedAccrualDocument.id],
            }),
        );
    }, [
        closeDetails,
        linkedAccrualDocument.id,
        navigate,
        shownDocumentId,
        type,
    ]);

    return (
        <FinancialDocumentLinkRow
            dateIso={linkedAccrualDocument.date}
            description={formatAccrualDescription(linkedAccrualDocument)}
            testId={`financial-document-${type}-item`}
            dateTestId={`financial-document-${type}-item-date`}
            descriptionTestId={`financial-document-${type}-item-description`}
            view={{
                onClick: handleClick,
                tooltip: type === "invoice" ? "View invoice" : "View bill",
                testId: `financial-document-${type}-view-button`,
            }}
        />
    );
};
